import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@repo/ui/src/components/ui/sheet';
import { Button } from '@repo/ui/src/components/ui/button';
import { Label } from '@repo/ui/src/components/ui/label';
import { Input } from '@repo/ui/src/components/ui/input';
import { Textarea } from '@repo/ui/src/components/ui/textarea';
import { Checkbox } from '@repo/ui/src/components/ui/checkbox';
import { Switch } from '@repo/ui/src/components/ui/switch';
import type { AdminPromotionDetail } from '@repo/shared-types';
import { useCreatePromotion, useUpdatePromotion } from '../../hooks/usePromotions';
import { toVnDatetimeLocal, fromVnDatetimeLocal } from '../../lib/adminFormat';

const PLAN_CODE_OPTIONS = [
  { value: 'PLUS', label: 'Plus' },
  { value: 'PRO', label: 'Pro' },
  { value: 'CENTER', label: 'Center' },
] as const;

type PlanCode = (typeof PLAN_CODE_OPTIONS)[number]['value'];

interface PromotionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  /** Required (and read) only when `mode === 'edit'`. */
  promotion?: AdminPromotionDetail;
}

/** A fresh campaign's default window: now through 30 days out, both in VN wall time. */
function defaultWindow(): { startsAt: string; endsAt: string } {
  const now = new Date();
  const later = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  return { startsAt: toVnDatetimeLocal(now.toISOString()), endsAt: toVnDatetimeLocal(later.toISOString()) };
}

export function PromotionSheet({ open, onOpenChange, mode, promotion }: PromotionSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{mode === 'create' ? 'Tạo ưu đãi' : 'Sửa ưu đãi'}</SheetTitle>
          <SheetDescription>
            {mode === 'create'
              ? 'Giảm giá theo phần trăm cho gói Plus, Pro hoặc Center. Để trống mã nếu đây là chiến dịch tự áp dụng, khách không cần nhập mã.'
              : 'Giờ bắt đầu và kết thúc nhập theo giờ Việt Nam.'}
          </SheetDescription>
        </SheetHeader>
        <PromotionForm
          key={open ? (mode === 'edit' ? (promotion?.id ?? 'edit') : 'create') : 'closed'}
          mode={mode}
          promotion={promotion}
          onOpenChange={onOpenChange}
        />
      </SheetContent>
    </Sheet>
  );
}

function PromotionForm({
  mode,
  promotion,
  onOpenChange,
}: {
  mode: 'create' | 'edit';
  promotion?: AdminPromotionDetail;
  onOpenChange: (open: boolean) => void;
}) {
  const initialWindow =
    mode === 'edit' && promotion
      ? { startsAt: toVnDatetimeLocal(promotion.startsAt), endsAt: toVnDatetimeLocal(promotion.endsAt) }
      : defaultWindow();

  const [name, setName] = useState(promotion?.name ?? '');
  const [headline, setHeadline] = useState(promotion?.headline ?? '');
  const [code, setCode] = useState(promotion?.code ?? '');
  const [percentOff, setPercentOff] = useState(String(promotion?.percentOff ?? 20));
  const [startsAt, setStartsAt] = useState(initialWindow.startsAt);
  const [endsAt, setEndsAt] = useState(initialWindow.endsAt);
  const [planCodes, setPlanCodes] = useState<Set<PlanCode>>(
    new Set(promotion ? (promotion.planCodes as PlanCode[]) : (['PLUS', 'PRO', 'CENTER'] as PlanCode[])),
  );
  const [appliesToMonthly, setAppliesToMonthly] = useState(promotion?.cycles.includes('MONTHLY') ?? true);
  const [appliesToAnnual, setAppliesToAnnual] = useState(promotion?.cycles.includes('ANNUAL') ?? true);
  const [active, setActive] = useState(promotion?.active ?? true);
  const [ownerLabel, setOwnerLabel] = useState(promotion?.ownerLabel ?? '');
  const [maxRedemptions, setMaxRedemptions] = useState(
    promotion?.maxRedemptions != null ? String(promotion.maxRedemptions) : '',
  );
  const [note, setNote] = useState(promotion?.note ?? '');

  const createPromotion = useCreatePromotion();
  // Called unconditionally (rules of hooks): in create mode the id is empty
  // and the mutation it builds is simply never invoked.
  const updatePromotion = useUpdatePromotion(promotion?.id ?? '');
  const isPending = mode === 'create' ? createPromotion.isPending : updatePromotion.isPending;

  // The API 409s (CODE_IMMUTABLE) once ANY invoice references the promotion,
  // EXPIRED included (adminPromotions.ts's PATCH handler). Fix wave (B4):
  // this used to gate on `redemptionCount` (PENDING+PAID only), so a
  // promotion with only an EXPIRED redemption showed an editable code field
  // the server would then reject with 409 — `invoiceCount` is the same
  // unfiltered count the server itself checks.
  const codeLocked = mode === 'edit' && (promotion?.invoiceCount ?? 0) > 0;

  const percentValue = Number(percentOff);
  const isValidPercent = Number.isInteger(percentValue) && percentValue >= 1 && percentValue <= 90;
  const isValidWindow = !!startsAt && !!endsAt && fromVnDatetimeLocal(startsAt) < fromVnDatetimeLocal(endsAt);
  const hasPlan = planCodes.size > 0;
  const hasCycle = appliesToMonthly || appliesToAnnual;
  const canSubmit = !isPending && !!name.trim() && isValidPercent && isValidWindow && hasPlan && hasCycle;

  function togglePlanCode(value: PlanCode, checked: boolean) {
    setPlanCodes((prev) => {
      const next = new Set(prev);
      if (checked) next.add(value);
      else next.delete(value);
      return next;
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    const shared = {
      name: name.trim(),
      headline: headline.trim() || null,
      percentOff: percentValue,
      startsAt: fromVnDatetimeLocal(startsAt),
      endsAt: fromVnDatetimeLocal(endsAt),
      planCodes: Array.from(planCodes),
      appliesToMonthly,
      appliesToAnnual,
      active,
      ownerLabel: ownerLabel.trim() || null,
      maxRedemptions: maxRedemptions ? Number(maxRedemptions) : null,
      note: note.trim() || null,
    };

    if (mode === 'create') {
      createPromotion.mutate(
        { ...shared, kind: 'PERCENT_OFF', code: code.trim() || null },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      updatePromotion.mutate(
        { ...shared, ...(codeLocked ? {} : { code: code.trim() || null }) },
        { onSuccess: () => onOpenChange(false) },
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 py-4">
      {mode === 'edit' && (
        <div className="space-y-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <p>
            Đổi phần trăm không làm thay đổi hoá đơn đã tạo. Muốn khách hưởng mức mới thì huỷ hoá đơn cũ rồi nâng gói
            lại.
          </p>
          <p>Rút ngắn thời gian hoặc tắt ưu đãi sẽ thu lại những suất chưa ai dùng.</p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="promo-name">Tên ưu đãi</Label>
        <Input id="promo-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="promo-headline">Câu quảng cáo (tuỳ chọn)</Label>
        <Input
          id="promo-headline"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          placeholder="VD: Giảm 30% kỳ đầu cho mọi gói"
          maxLength={120}
        />
        <p className="text-xs text-muted-foreground">
          Hiện trên landing và banner trong app. Để trống thì dùng câu tự sinh.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="promo-code">Mã (để trống nếu là chiến dịch tự áp dụng)</Label>
        <Input
          id="promo-code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          disabled={codeLocked}
          placeholder="VD: KHAIGIANG30"
          maxLength={20}
        />
        {codeLocked && (
          <p className="text-xs text-muted-foreground">
            Mã đã được dùng trên hoá đơn nên không đổi được. Tạo ưu đãi mới nếu cần mã khác.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="promo-percent">Giảm (%)</Label>
        <Input
          id="promo-percent"
          type="number"
          min={1}
          max={90}
          value={percentOff}
          onChange={(e) => setPercentOff(e.target.value)}
          required
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="promo-starts">Bắt đầu</Label>
          <input
            id="promo-starts"
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="promo-ends">Kết thúc</Label>
          <input
            id="promo-ends"
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            required
          />
        </div>
      </div>
      {/* A datetime-local whose typed day does not exist (31/09) reports an
          EMPTY value, not an invalid one, so the submit button went grey with
          no explanation. Say which field is the problem. */}
      {(!startsAt || !endsAt) && (
        <p className="text-xs text-destructive">
          {!startsAt && !endsAt
            ? 'Nhập ngày bắt đầu và ngày kết thúc. Ngày không tồn tại (ví dụ 31/09) bị coi là trống.'
            : !startsAt
              ? 'Ngày bắt đầu chưa hợp lệ. Ngày không tồn tại (ví dụ 31/09) bị coi là trống.'
              : 'Ngày kết thúc chưa hợp lệ. Ngày không tồn tại (ví dụ 31/09) bị coi là trống.'}
        </p>
      )}
      {!isValidWindow && startsAt && endsAt && (
        <p className="text-xs text-destructive">Ngày bắt đầu phải trước ngày kết thúc.</p>
      )}

      <div className="space-y-2">
        <Label>Áp dụng cho gói</Label>
        <div className="flex flex-col gap-2">
          {PLAN_CODE_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex min-h-[28.8px] items-center gap-[9.6px]">
              <Checkbox
                checked={planCodes.has(opt.value)}
                onCheckedChange={(v) => togglePlanCode(opt.value, v === true)}
                className="size-[18px] shrink-0"
              />
              <span className="text-sm text-surface-800">{opt.label}</span>
            </label>
          ))}
        </div>
        {!hasPlan && <p className="text-xs text-destructive">Chọn ít nhất một gói.</p>}
      </div>

      <div className="space-y-2">
        <Label>Áp dụng cho chu kỳ</Label>
        <div className="flex flex-col gap-2">
          <label className="flex min-h-[28.8px] items-center gap-[9.6px]">
            <Checkbox
              checked={appliesToMonthly}
              onCheckedChange={(v) => setAppliesToMonthly(v === true)}
              className="size-[18px] shrink-0"
            />
            <span className="text-sm text-surface-800">Theo tháng</span>
          </label>
          <label className="flex min-h-[28.8px] items-center gap-[9.6px]">
            <Checkbox
              checked={appliesToAnnual}
              onCheckedChange={(v) => setAppliesToAnnual(v === true)}
              className="size-[18px] shrink-0"
            />
            <span className="text-sm text-surface-800">Theo năm</span>
          </label>
        </div>
        {!hasCycle && <p className="text-xs text-destructive">Chọn ít nhất một chu kỳ thanh toán.</p>}
      </div>

      <div className="flex items-center justify-between">
        <Label htmlFor="promo-active">Đang chạy</Label>
        <Switch id="promo-active" checked={active} onCheckedChange={setActive} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="promo-owner">Chủ sở hữu (tuỳ chọn)</Label>
        <Input
          id="promo-owner"
          value={ownerLabel}
          onChange={(e) => setOwnerLabel(e.target.value)}
          placeholder="VD: đối tác, sự kiện khai giảng..."
          maxLength={80}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="promo-max">Giới hạn lượt dùng (tuỳ chọn)</Label>
        <Input
          id="promo-max"
          type="number"
          min={1}
          value={maxRedemptions}
          onChange={(e) => setMaxRedemptions(e.target.value)}
          placeholder="Để trống nếu không giới hạn"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="promo-note">Ghi chú nội bộ (tuỳ chọn)</Label>
        <Textarea
          id="promo-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Ghi chú chỉ admin thấy được"
          rows={2}
          maxLength={500}
        />
      </div>

      <SheetFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4">
        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
          Huỷ
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {isPending ? 'Đang lưu...' : mode === 'create' ? 'Tạo ưu đãi' : 'Lưu thay đổi'}
        </Button>
      </SheetFooter>
    </form>
  );
}
